import {
  collection,
  getDocs,
  addDoc,
  query,
  where,
  doc
} from 'firebase/firestore';
import { db } from '@lib/firebase';

import { ValidationError, ModuleError } from '@/shared/api/errors/ApiError';
import { getTimestamps, getUpdatedTimestamp } from '@/shared/utils/api/timestampHelper';
import ServiceBase from '@/shared/api/base/ServiceBase';

const MODULES_COLLECTION = 'modules';
const CACHE_TTL = 60 * 60 * 1000; // 1 hour for modules

export interface Module {
  id: string;
  courseId: string;
  title: string;
  description: string;
  order: number;
  lessonOrder?: string[];
  createdAt?: string;
  updatedAt?: string;
  lessonsCount?: number;
  [key: string]: any;
}

class ModuleService extends ServiceBase {
  constructor() {
    super('ModuleService');
  }

  /**
   * Get all modules for a course
   */
  async getModules(courseId: string): Promise<Module[]> {
    try {
      this.validate.validateCourseId(courseId);

      // E2E Test Mock Injection
      if (typeof window !== 'undefined' && (window as any).MOCK_MODULES) {
        this.log('Returning MOCKED Modules for E2E testing');
        return ((window as any).MOCK_MODULES as Module[])
          .filter(m => m.courseId === courseId)
          .sort((a, b) => (a.order || 0) - (b.order || 0));
      }

      const cacheKey = `modules_${courseId}`;
      const cached = this.cache.get<Module[]>(cacheKey);
      if (cached) return cached;

      const modulesRef = collection(db, MODULES_COLLECTION);
      const q = query(
        modulesRef,
        where('courseId', '==', courseId)
      );
      const querySnapshot = await getDocs(q);

      const modules = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Module[];

      modules.sort((a, b) => (a.order || 0) - (b.order || 0));
      this.cache.set(cacheKey, modules, CACHE_TTL);
      return modules;
    } catch (error) {
      this.logError(error, { method: 'getModules', courseId });
      throw error;
    }
  }

  /**
   * Get module by ID
   */
  async getModuleById(moduleId: string): Promise<Module> {
    try {
      this.validate.validateModuleId(moduleId);

      // E2E Test Mock Injection
      if (typeof window !== 'undefined' && (window as any).MOCK_MODULES) {
        const mockModule = ((window as any).MOCK_MODULES as Module[]).find(m => m.id === moduleId);
        if (mockModule) {
          this.log('Returning MOCKED Module by ID for E2E testing');
          return mockModule;
        }
      }

      const cacheKey = `module_${moduleId}`;
      const cached = this.cache.get<Module>(cacheKey);
      if (cached) return cached;

      const moduleData = await this.getDoc(MODULES_COLLECTION, moduleId) as Module;

      this.cache.set(cacheKey, moduleData, CACHE_TTL);
      return moduleData;
    } catch (error: any) {
      if (error.code === 'NOT_FOUND') {
        throw new ModuleError('Module not found');
      }
      this.logError(error, { method: 'getModuleById', moduleId });
      throw error;
    }
  }

  /**
   * Create new module
   */
  async createModule(moduleData: Partial<Module>): Promise<Module> {
    try {
      if (typeof moduleData !== 'object' || !moduleData) {
        throw new ValidationError('Module data must be a valid object');
      }

      const modulesRef = collection(db, MODULES_COLLECTION);
      const newModule = {
        ...moduleData,
        ...getTimestamps()
      };

      const docRef = await addDoc(modulesRef, newModule);
      this.cache.invalidate(`modules_${moduleData.courseId}`);

      return {
        id: docRef.id,
        ...newModule
      } as Module;
    } catch (error) {
      this.logError(error, { method: 'createModule' });
      throw error;
    }
  }

  /**
   * Update module
   */
  async updateModule(moduleId: string, updates: Partial<Module>): Promise<Module> {
    try {
      this.validate.validateModuleId(moduleId);
      if (typeof updates !== 'object' || !updates) {
        throw new ValidationError('Updates must be a valid object');
      }

      const updateData = {
        ...updates,
        ...getUpdatedTimestamp()
      };

      await this.updateDoc(MODULES_COLLECTION, moduleId, updateData);

      // Invalidate caches
      this.cache.invalidate(`module_${moduleId}`);
      const module = await this.getModuleById(moduleId);
      this.cache.invalidate(`modules_${module.courseId}`);

      return module;
    } catch (error) {
      this.logError(error, { method: 'updateModule', moduleId });
      throw error;
    }
  }

  /**
   * Delete module
   */
  async deleteModule(moduleId: string): Promise<void> {
    try {
      this.validate.validateModuleId(moduleId);

      const module = await this.getModuleById(moduleId);
      await this.deleteDoc(MODULES_COLLECTION, moduleId);

      this.cache.invalidate(`module_${moduleId}`);
      this.cache.invalidate(`modules_${module.courseId}`);
    } catch (error) {
      this.logError(error, { method: 'deleteModule', moduleId });
      throw error;
    }
  }

  /**
   * Reorder modules
   */
  async reorderModules(courseId: string, moduleOrders: { moduleId: string; order: number }[]): Promise<void> {
    try {
      this.validate.validateCourseId(courseId);
      if (!Array.isArray(moduleOrders)) {
        throw new ValidationError('Module orders must be an array');
      }

      const batch = this.batch();

      moduleOrders.forEach(({ moduleId, order }) => {
        const moduleRef = doc(db, MODULES_COLLECTION, moduleId);
        batch.update(moduleRef, {
          order,
          ...getUpdatedTimestamp()
        });
        this.cache.invalidate(`module_${moduleId}`);
      });

      await batch.commit();
      this.cache.invalidate(`modules_${courseId}`);
    } catch (error) {
      this.logError(error, { method: 'reorderModules', courseId });
      throw error;
    }
  }

  /**
   * Get module with lessons count
   */
  async getModuleWithStats(moduleId: string): Promise<Module> {
    try {
      this.validate.validateModuleId(moduleId);

      const module = await this.getModuleById(moduleId);

      const lessonsRef = collection(db, 'lessons');
      const q = query(lessonsRef, where('moduleId', '==', moduleId));
      const lessonsSnapshot = await getDocs(q);

      return {
        ...module,
        lessonsCount: lessonsSnapshot.size
      };
    } catch (error) {
      this.logError(error, { method: 'getModuleWithStats', moduleId });
      throw error;
    }
  }
}

const moduleServices = new ModuleService();

// Maintain backward compatibility for named exports
export const getModules = (id: string) => moduleServices.getModules(id);
export const getModuleById = (id: string) => moduleServices.getModuleById(id);
export const createModule = (data: Partial<Module>) => moduleServices.createModule(data);
export const updateModule = (id: string, updates: Partial<Module>) => moduleServices.updateModule(id, updates);
export const deleteModule = (id: string) => moduleServices.deleteModule(id);
export const reorderModules = (id: string, orders: { moduleId: string; order: number }[]) => 
  moduleServices.reorderModules(id, orders);
export const getModuleWithStats = (id: string) => moduleServices.getModuleWithStats(id);

export default moduleServices;
